<template>
  <div class="wrapper">
    <sp-button
      v-if="showDeleteButton"
      slot="delete-button"
      icon="close-bold"
      color="danger"
      size="xx-small"
      class="layout-builder-row-container__remove-button"
      @click.stop="deleteRow(id)"
    >
    </sp-button>
    <sp-layout-row class="layout-builder-row-container" :class="modifierClass" v-bind="row" @click="toggleSelected">
      <sp-col
        v-for="(column, i) in row.columns"
        :key="column.id"
        :cols="column.cols"
        :cols-sm="column.colsSm"
        :foreground="column.foreground"
      >
        <div class="layout-builder-row-container__column">
          <span class="column-label">
            {{ column.label ?? `Column ${i + 1}` }}
          </span>
        </div>
      </sp-col>
    </sp-layout-row>
    <div class="layout-builder-row-container__row-index-tab" part="row-index-tab" @click="toggleSelected">
      {{ index + 1 }}
    </div>
  </div>
</template>

<script setup>
import { watchOnce } from "@vueuse/core";
import { computed, ref } from "vue";
import { Context } from "../../constants";
import { useLayoutBuilderStore } from "../../store/layout-builder";

const { selectedRowId, deleteRow, permitDeleteRow, mostRecentRowId } = useLayoutBuilderStore({
  context: Context.IFRAME,
});

const props = defineProps({
  row: {
    type: Object,
    required: true,
  },
  index: {
    type: [Number, String],
    required: true,
  },
});

const id = computed(() => props.row.id);
const flash = ref(false);
const isSelected = computed(() => id.value === selectedRowId.value);

const showDeleteButton = computed(() => permitDeleteRow.value && isSelected.value);

const modifierClass = computed(() => ({
  "--flash": flash.value,
  "--selected": isSelected.value,
}));

watchOnce(id, handleIdChange);

function handleIdChange() {
  if (mostRecentRowId.value === id.value) {
    startFlash();
    mostRecentRowId.value = undefined;
  }
}

function startFlash() {
  flash.value = true;
  setTimeout(() => (flash.value = false), 3000);
}

function toggleSelected() {
  selectedRowId.value = isSelected.value ? undefined : id.value;
}
</script>

<style scoped>
.wrapper {
  position: relative;
}

.layout-builder-row-container {
  position: relative;
  background: hsla(300, 71%, 97%, 0.8);

  &.--flash {
    animation: vibrantFlash 0.5s ease-in-out;
  }

  &::part(wrapper) {
    outline: 1px dashed hsla(340, 70%, 50%, 0.2);
    outline-offset: -1px;
  }

  &.--selected {
    z-index: 999;
    box-shadow: 0px 0px 10px hsla(340, 10%, 10%, 0.2);

    &::part(wrapper) {
      outline: 1px dashed hsla(340, 70%, 50%, 0.8);
    }
  }
}

.layout-builder-row-container__row-index-tab {
  font-size: var(--sp-ref-font-size-small, 0.875rem);
  font-weight: 700;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 2rem;
  height: 4rem;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  left: var(--row-index-tab-left, -2rem);
  background: hsl(347, 94%, 87%);
  color: white;
  outline: 1px solid hsl(352, 52%, 80%);
  line-height: 1;
  cursor: pointer;
  border-radius: var(--row-index-tab-radius, 4px 0 0 4px);
  z-index: 999;
}

.layout-builder-row-container.--selected + .layout-builder-row-container__row-index-tab {
  background: hsla(340, 70%, 50%, 0.8);
}

.layout-builder-row-container__remove-button {
  position: absolute;
  top: 0;
  left: 0;
  transform: translate(-50%, -50%);
  z-index: 1000;
  outline: 1px solid white;
}

.layout-builder-row-container__column {
  display: flex;
  align-items: start;
  justify-content: center;
  text-align: center;
  min-height: 200px;
  background: transparent;
  color: rgba(217, 38, 98, 0.5);
  outline: 1px solid hsl(351, 76%, 87%);
  outline-offset: -1px;
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem;

  .column-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
    padding-top: 1rem;
  }
}

sp-col {
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>

<style>
@keyframes vibrantFlash {
  0% {
    background-color: hsla(340, 70%, 50%, 0.05);
  }
  30% {
    background-color: hsla(340, 70%, 50%, 0.5);
  }
  100% {
    background-color: hsla(340, 70%, 50%, 0.2);
  }
}
</style>
